import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';

// Helper function to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  category?: string;
  custom_label_0?: string;
  image_url?: string;
  status: 'active' | 'inactive';
  whatsapp_product_id?: string;
  retailer_id?: string; // This is the SKU/content_id needed for product messages
  created_at: string;
  updated_at: string;
}

export interface CatalogInfo {
  id: string;
  name: string;
  business_account_id: string;
  whatsapp_catalog_id: string;
  status: 'connected' | 'disconnected' | 'syncing';
  last_sync_at?: string;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface AvailableCatalog {
  id: string;
  name: string;
  product_count: number;
  status: string;
  whatsapp_catalog_id?: string;
}

export interface CommerceStats {
  total_revenue: number;
  total_orders: number;
  total_products: number;
  avg_order_value: number;
  revenue_change: number;
  orders_change: number;
  products_change: number;
  aov_change: number;
  top_products: Array<{
    productName: string;
    totalSold: number;
    totalRevenue: number;
  }>;
  sales_history: Array<{
    name: string;
    date: string;
    sales: number;
    orders: number;
  }>;
  sources?: {
    native: { revenue: number; orders: number; products: number };
    shopify: { revenue: number; orders: number; products: number };
    woocommerce: { revenue: number; orders: number; products: number };
  };
}

export interface Order {
  id: string;
  order_number: string;
  source: 'native' | 'shopify' | 'woocommerce';
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  total_amount: number;
  currency: string;
  status: string;
  payment_status: string;
  created_at: string;
  items_count: number;
  source_id?: string;
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: { orderId: string; status: string; syncPetpooja?: boolean }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders/${params.orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          status: params.status,
          syncPetpooja: params.syncPetpooja ?? true,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to update order');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
    },
  });
}

// Hook to fetch unified orders
export function useOrders(filters?: {
  search?: string;
  status?: string;
  source?: string;
  page?: number;
  limit?: number;
}) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{
    orders: Order[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['orders', selectedAccountId, filters],
    queryFn: async () => {
      if (!selectedAccountId) return { orders: [], total: 0, page: 1, limit: 20 };

      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.source && { source: filters.source }),
        page: (filters?.page || 1).toString(),
        limit: (filters?.limit || 20).toString(),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/orders?${params}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch orders');
      }
      const data = await response.json();
      const totalFromPagination =
        typeof data?.pagination?.total === 'number' ? data.pagination.total :
          (typeof data?.total === 'number' ? data.total : null);
      return {
        orders: Array.isArray(data.data) ? data.data : [],
        total: totalFromPagination ?? (Array.isArray(data.data) ? data.data.length : 0),
        page: data.page || 1,
        limit: data.limit || 20
      };
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch available catalogs from Meta
export function useAvailableCatalogs(options?: { sync?: boolean }) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<AvailableCatalog[]>({
    queryKey: ['available-catalogs', selectedAccountId, options?.sync],
    queryFn: async () => {
      if (!selectedAccountId) return [];

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      if (options?.sync) {
        params.append('sync', 'true');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error('Failed to fetch available catalogs');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch catalog information
export function useCatalogInfo() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<CatalogInfo | null>({
    queryKey: ['catalog-info', selectedAccountId],
    queryFn: async () => {
      if (!selectedAccountId) return null;


      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/info?businessAccountId=${selectedAccountId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch catalog info');
      }
      const data = await response.json();
      return data.data || null; // Extract data from response
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch products
export function useProducts(filters?: {
  search?: string;
  category?: string;
  status?: string;
  page?: number;
  limit?: number;
  catalogId?: string; // Add catalogId param
  enabled?: boolean; // Caller opt-out (e.g. when the WhatsApp-catalog tab isn't active)
}) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products', selectedAccountId, filters],
    queryFn: async () => {
      if (!selectedAccountId) return { products: [], total: 0, page: 1, limit: 20 };

      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        ...(filters?.search && { search: filters.search }),
        ...(filters?.category && { category: filters.category }),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.catalogId && { catalogId: filters.catalogId }), // Pass catalogId
        page: (filters?.page || 1).toString(),
        limit: (filters?.limit || 20).toString(),
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page: 1, limit: 20 };
    },
    enabled: !!selectedAccountId && filters?.enabled !== false,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch commerce statistics
export function useCommerceStats(options?: { from?: Date; to?: Date }) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<CommerceStats>({
    queryKey: ['commerce-stats', selectedAccountId, options?.from, options?.to],
    queryFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      // Send dates as YYYY-MM-DD strings to avoid timezone shift
      // (toISOString converts to UTC which shifts the date in IST/other timezones)
      if (options?.from) {
        const d = options.from;
        params.append('startDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      if (options?.to) {
        const d = options.to;
        params.append('endDate', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/stats?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch commerce stats');
      }
      const data = await response.json();
      return data.data || {
        total_revenue: 0,
        total_orders: 0,
        total_products: 0,
        avg_order_value: 0,
        revenue_change: 0,
        products_change: 0,
        aov_change: 0,
        top_products: [],
        sales_history: []
      };
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to setup catalog
export function useSetupCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (catalogId: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          catalogId,
          businessAccountId: selectedAccountId,
        }),
      });

      if (!response.ok) {
        // Preserve the backend's code/details/statusCode so the friendly-error
        // layer (showApiError) can surface the specific reason, not the generic label.
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to setup catalog');
      }

      return response.json();
    },
    // Optimistic update for instant UI feedback
    onMutate: async (catalogId) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['catalog-info', selectedAccountId] });
      await queryClient.cancelQueries({ queryKey: ['available-catalogs', selectedAccountId] });

      // Snapshot the previous value
      const previousCatalogInfo = queryClient.getQueryData(['catalog-info', selectedAccountId]);
      const availableCatalogs = queryClient.getQueryData<AvailableCatalog[]>(['available-catalogs', selectedAccountId]);

      // Optimistically update to the new catalog
      if (availableCatalogs) {
        const selectedCatalog = availableCatalogs.find(cat => cat.id === catalogId);
        if (selectedCatalog) {
          queryClient.setQueryData(['catalog-info', selectedAccountId], {
            id: catalogId,
            name: selectedCatalog.name,
            business_account_id: selectedAccountId,
            whatsapp_catalog_id: catalogId,
            status: 'connected',
            product_count: selectedCatalog.product_count,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      }

      // Return context with previous value for rollback
      return { previousCatalogInfo };
    },
    // On error, rollback to previous value
    onError: (err, catalogId, context) => {
      if (context?.previousCatalogInfo) {
        queryClient.setQueryData(['catalog-info', selectedAccountId], context.previousCatalogInfo);
      }
    },
    // Always refetch after success or error to ensure data is in sync
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs'] });
    },
  });
}



// Hook to create a new catalog
export function useCreateCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (name: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalogs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate available catalogs to refresh the list
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
    },
  });
}

// Hook to sync catalog
export function useSyncCatalog() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          catalogId, // Pass catalogId if provided
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to sync catalog');
      }

      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['catalog-info'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats'] });

      // Return the response data so the component can show the message
      return data;
    },
  });
}

// Hook to re-link existing catalog to WABA (repairs broken linkage without a full reconnect)
export function useRelinkCatalogToWaba() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/relink-waba`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: selectedAccountId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to re-link catalog to WhatsApp');
      }

      return response.json();
    },
  });
}

// Hook to delete catalog
export function useDeleteCatalog() {
  const { selectedAccountId } = useBusinessAccountContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (catalogId?: string) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      // If catalogId provided, add to query string
      let url = `${API_CONFIG.BASE_URL}/commerce/catalog?businessAccountId=${selectedAccountId}`;
      if (catalogId) {
        url += `&catalogId=${catalogId}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch commerce data
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
    },
  });
}

// Hook to rename catalog
export function useRenameCatalog() {
  const { selectedAccountId } = useBusinessAccountContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ catalogId, name }: { catalogId: string; name: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/catalog/${catalogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to rename catalog');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-catalogs', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
    },
  });
}

// Hook to fetch product sets
export function useProductSets(catalogId?: string) {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{ id: string; name: string; product_count: number }[]>({
    queryKey: ['product-sets', selectedAccountId, catalogId],
    queryFn: async () => {
      if (!selectedAccountId) return [];

      const params = new URLSearchParams({ businessAccountId: selectedAccountId });
      if (catalogId) params.append('catalogId', catalogId);

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch product sets');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!selectedAccountId,
    staleTime: 0, // Disable caching
  });
}

// Hook to fetch products by product set
export function useProductsBySet(setId?: string, opts?: { page?: number; limit?: number }) {
  const { selectedAccountId } = useBusinessAccountContext();
  const page = opts?.page ?? 1;
  const limit = opts?.limit ?? 20;

  return useQuery<{
    products: Product[];
    total: number;
    page: number;
    limit: number;
  }>({
    queryKey: ['products-by-set', selectedAccountId, setId, page, limit],
    queryFn: async () => {
      if (!selectedAccountId || !setId) return { products: [], total: 0, page, limit };
      const params = new URLSearchParams({
        businessAccountId: selectedAccountId,
        page: String(page),
        limit: String(limit),
      });
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets/${setId}/products?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to fetch products by set');
      }
      const data = await response.json();
      return data.data || { products: [], total: 0, page, limit };
    },
    enabled: !!selectedAccountId && !!setId,
    staleTime: 0, // Disable caching
  });
}

// Hook to bulk import products from Excel/CSV
export function useBulkImportProducts() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: {
      products: Array<{
        name: string;
        description: string;
        price: number;
        currency: string;
        category: string;
        availability: string;
        condition: string;
        brand?: string;
        url?: string;
        image_url: string | string[];
        retailer_id?: string;
      }>;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          products: data.products,
          catalogId: data.catalogId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to bulk import products');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to add a new product
export function useAddProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (productData: {
      name: string;
      description: string;
      price: number;
      currency: string;
      category: string;
      availability: string;
      condition: string;
      brand?: string;
      url?: string;
      image_url?: string[];
      sets?: string[];
      newSetName?: string;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...productData,
          businessAccountId: selectedAccountId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create product');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch products
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to delete a product
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: { whatsappProductId: string; catalogId?: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      let url = `${API_CONFIG.BASE_URL}/commerce/products/${encodeURIComponent(params.whatsappProductId)}?businessAccountId=${selectedAccountId}`;
      if (params.catalogId) {
        url += `&catalogId=${encodeURIComponent(params.catalogId)}`;
      }
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errorData, statusCode: response.status }, 'Failed to delete product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
    },
  });
}

// Hook to fetch business profile settings
export function useBusinessProfile() {
  const { selectedAccountId } = useBusinessAccountContext();

  return useQuery<{ is_cart_enabled: boolean; is_catalog_visible: boolean }>({
    queryKey: ['business-profile-settings', selectedAccountId],
    queryFn: async () => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile?businessAccountId=${selectedAccountId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch business profile settings');
      }

      const data = await response.json();
      return data.data;
    },
    enabled: !!selectedAccountId,
  });
}

// Hook to update business profile settings
export function useUpdateBusinessProfile() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (settings: { is_cart_enabled?: boolean; is_catalog_visible?: boolean }) => {
      if (!selectedAccountId) throw new Error('No business account selected');

      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/settings/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          settings,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update settings');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-profile-settings', selectedAccountId] });
    },
  });
}

// Hook to update a product
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (params: {
      whatsappProductId: string;
      updates: any;
      catalogId?: string;
    }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/products/${params.whatsappProductId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ businessAccountId: selectedAccountId, ...params.updates, catalogId: params.catalogId })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-info', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['commerce-stats', selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId] });
    }
  });
}

// Hook to create a product set
export function useCreateProductSet() {
  const queryClient = useQueryClient();
  const { selectedAccountId } = useBusinessAccountContext();
  return useMutation({
    mutationFn: async (params: { name: string; catalogId?: string }) => {
      if (!selectedAccountId) throw new Error('No business account selected');
      const response = await fetch(`${API_CONFIG.BASE_URL}/commerce/product-sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          businessAccountId: selectedAccountId,
          name: params.name,
          catalogId: params.catalogId
        })
      });
      if (!response.ok) throw new Error('Failed to create set');
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId, variables.catalogId] });
      // Also invalidate default query just in case
      queryClient.invalidateQueries({ queryKey: ['product-sets', selectedAccountId] });
    },
  });
}
